import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Loader2, RotateCcw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import { generateProfileAvatar, resetProfileAvatar } from '../services/profileService';

/**
 * Generate the user's OWN profile picture (charges credits). When they're out we DON'T gate —
 * we show a soft, conversational invite to top up and let them decide (per the no-pressure rule).
 */
const ProfileAvatarEditor: React.FC = () => {
  const { user, refreshProfile } = useAuth();
  const { refresh } = useWallet();
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(false);
  const [error, setError] = useState('');
  // Only offer "revert" when a custom (generated) picture is actually set.
  const hasCustomAvatar = !!(user as { custom_avatar?: boolean } | null)?.custom_avatar;

  const generate = async () => {
    setBusy(true); setError(''); setInvite(false);
    try {
      await generateProfileAvatar(prompt.trim() || undefined);
      await refreshProfile();
      refresh?.();
    } catch (e: any) {
      if (/NOT_ENOUGH_CREDITS|NOT_ENOUGH_MP/i.test(e?.message || '')) setInvite(true);
      else setError(e?.message || 'Could not create your picture');
    } finally { setBusy(false); }
  };

  // Didn't like the generated one? Drop back to the social sign-in photo (or initials).
  const revert = async () => {
    setBusy(true); setError(''); setInvite(false);
    try {
      await resetProfileAvatar();
      await refreshProfile();
    } catch (e: any) {
      setError(e?.message || 'Could not revert your picture');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-secondary-200 dark:border-secondary-700 pt-4">
      <label className="text-xs font-medium uppercase tracking-wide text-secondary-500 dark:text-secondary-400">Make your own profile picture</label>
      <p className="mt-0.5 text-xs text-secondary-400">A custom picture replaces your sign-in photo everywhere — including what people in your chats see.</p>
      <div className="mt-2 flex gap-2">
        <input value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={300}
          placeholder="Describe the look — e.g. a calm minimalist portrait, monochrome"
          className="flex-1 min-w-0 rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
        <button disabled={busy} onClick={generate}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-3 py-2 text-sm font-medium text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Generate
        </button>
      </div>
      {hasCustomAvatar && (
        <button disabled={busy} onClick={revert}
          title="Revert to your sign-in photo"
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-secondary-500 hover:text-secondary-900 dark:text-secondary-400 dark:hover:text-white disabled:opacity-40 transition-colors">
          <RotateCcw className="h-3.5 w-3.5" />
          Use my sign-in photo instead
        </button>
      )}
      {invite && (
        <p className="mt-2 text-xs text-secondary-500 dark:text-secondary-400">
          You&apos;re a little low on credits for this one — no rush. You can
          {' '}<Link to="/wallet" className="underline hover:text-secondary-900 dark:hover:text-white">top up</Link> whenever you feel like it.
        </p>
      )}
      {error && <p className="mt-2 text-xs text-secondary-500 dark:text-secondary-400">{error}</p>}
    </div>
  );
};

export default ProfileAvatarEditor;
